import { memo, useCallback } from 'react';
import { Link } from "react-router-dom";
import { IoIosClose } from 'react-icons/io';
import { FaObjectUngroup } from 'react-icons/fa';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import useDeviceLinks from '../hooks/useDeviceLinks';
import { Overlay } from '../../../components/table/formatters';
import { InfoCard, InfoCardItem } from '../../../components/InfoCard';
import LoadingSpinner from '../../../components/LoadingSpinner';
import ConfirmModal from '../../../components/ConfirmModal';
import AddLinkModal from './AddLinkModal';
import { getPlaceholderImageForDevice } from '../utils';

function DeviceLinks({ device }) {
  const { t } = useTranslation();

  const {
    loading,
    links = [],
    updateLinks,
    setPrimary,
  } = useDeviceLinks(device?.id, { fetch: true });

  const renderHeader = useCallback(() => {
    return (
      <span className="d-flex justify-content-between align-items-center">
        <span className="mr-1 d-flex">
          <span>{t('components.device-links.header')}</span>&nbsp;{loading && <LoadingSpinner size={1} />}
        </span>
        <AddLinkModal device={device} as="button" className="w-auto px-0" />
      </span>
    );
  }, [loading]);

  const handleDeleteLink = useCallback(async (serial) => {
    await updateLinks({
      links: [{
        serial, linked: false,
      }]
    });
  }, []);

  const handleSetPrimary = useCallback(async (deviceId) => {
    await setPrimary({ id: deviceId });
  }, []);

  return (
    <InfoCard heading={renderHeader()} headerClassName="flex-grow-1">
      <InfoCardItem heading={t('components.device-links.primary-device-link')}>
        <Overlay tooltip={t('components.device-links.tooltip')}>
          <div>
            {device.primary_device_link ? t('yes') : t('no')}
          </div>
        </Overlay>
      </InfoCardItem>
      <Button
        variant="light"
        size="sm"
        className="p-0"
        onClick={() => { handleSetPrimary(device.id); }}
        disabled={loading || device.primary_device_link}
      >
        {t('components.device-links.set-primary-device-link')}&nbsp;
        <FaObjectUngroup size="1.8em" />
      </Button>
      {links.map(link => {
        return (
          <InfoCardItem key={link.id}>
            <div className="d-flex align-items-center">
              <img
                src={getPlaceholderImageForDevice({ sensor_type: link.sensor_type })}
                alt="placeholder"
                className="mr-1"
                style={{height: '1.5em', marginLeft: '-10px'}}
              />
              <Link to={`/equipment/${link.id}/${link.sensor_type === 'fitpower' ? 'fitpower' : ''}`}>
                <div className="mb-1" style={{fontSize: '16px', lineHeight: 1}}>{link.title}</div>
                <div className="text-secondary" style={{ fontSize: '12px', lineHeight: 1 }}>{link.subtitle}</div>
              </Link>
              <ConfirmModal
                header={t('components.device-links.modal.header')}
                confirmText={t('components.device-links.modal.confirm-text')}
              >
                <Button
                  variant="light"
                  size="sm"
                  className="p-0"
                  onClick={() => { handleDeleteLink(link.serial); }}
                  disabled={loading}
                >
                  <IoIosClose size="1.8em" />
                </Button>
              </ConfirmModal>
            </div>
          </InfoCardItem>
        );
      })}
    </InfoCard>
  );
}

export default memo(DeviceLinks);
